import { cn } from "@/lib/utils";
import React, { useMemo } from "react";
import TextInput from "./TextInput";

export type SliderInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  min: number;
  max: number;
  error?: boolean;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: number | string;
  name: string;
  step?: number;
  baseHOCClassName?: string;
  onChange: (value: number) => void;
  hideInput?: boolean;
  formatLabel?: (value: number) => string;
};

const clamp = (val: number, min: number, max: number) => {
  if (isNaN(val)) return min;
  return Math.max(min, Math.min(max, val));
};

const SliderInput = React.forwardRef<HTMLInputElement, SliderInputProps>(
  (
    {
      onChange,
      value = 0,
      name,
      className = "",
      disabled,
      min,
      step,
      max,
      error,
      placeholder,
      hideInput,
      formatLabel
    },
    ref
  ) => {
    let numericValue = typeof value === "string" ? parseFloat(value) : value;
    numericValue = clamp(numericValue, min, max);

    const fillPercentage = useMemo(() => {
      const safeValue = clamp(numericValue, min, max);
      const percentage = ((safeValue - min) / (max - min)) * 100;
      return `${percentage}%`;
    }, [numericValue, min, max]);

    const handleChange = (val: number | string) => {
      let num = typeof val === "string" ? parseFloat(val) : val;
      num = clamp(num, min, max);
      onChange(num);
    };
    return (
      <div className="flex w-full flex-row items-center gap-2">
        {!hideInput && (
          <TextInput
            name={name}
            type="number"
            min={min}
            max={max}
            step={step}
            value={value?.toString() || min.toString()}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            ref={ref}
            className={cn(
              "subtext-x-small h-[32px] rounded-[6px] border border-gray-dark-200 bg-gray-dark-600 p-2 text-center text-gray-dark-2200 transition-colors placeholder:text-gray-dark-2200",
              className,
              error ? "border-red-dark-300" : ""
            )}
          />
        )}
        <div className="flex w-full flex-col gap-1 overflow-hidden pt-1">
          <div className="relative z-0 flex h-2 items-center">
            {/* Background Track with Gradient */}
            <div className="absolute z-0 h-2 w-full rounded-full bg-gradient-to-r from-gray-light-300 via-gray-light-300 to-gray-light-300 dark:from-gray-dark-500 dark:via-gray-dark-800 dark:to-gray-dark-700" />

            {/* Fill Track with Gradient */}
            <div
              className="absolute z-0 mx-[1px] h-[6px] rounded-full bg-gradient-to-r from-white to-[#0070F3] dark:from-black"
              style={{ width: fillPercentage }}
            />

            {/* Slider input component implementation */}
            <input
              name={name}
              type="range"
              onChange={(e) => handleChange(e.target.value as unknown as number)}
              min={min}
              max={max}
              step={step}
              value={value}
              disabled={disabled}
              className={cn(
                "custom-slider-thumb absolute z-0 h-full w-full cursor-pointer appearance-none bg-transparent"
              )}
            />
          </div>
          <div className="subtext-x-small relative z-0 flex flex-row items-center justify-between font-medium leading-4 text-gray-dark-2200">
            <span className="text-left">{formatLabel ? formatLabel(min) : min}</span>
            <span className="text-right">{formatLabel ? formatLabel(max) : max}</span>
          </div>
        </div>
      </div>
    );
  }
);

SliderInput.displayName = "SliderInput";

export default SliderInput;
